// import { Edit2, Trash2, ExternalLink, Copy } from "lucide-react";
import { RefreshCw } from "lucide-react";
import { momentum } from "ldrs";
import { invalidateWebsitesCache, useWebsitesStore } from "../../store/websitesStore";
import { useEffect, useRef, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
// import { Website } from "../../interfaces/websites";
import { decodeTokenService } from "../../services/decodeTokenService";
import { enqueueSnackbar } from "notistack";
import { deleteWebsite } from "../../services/generativeAI/generativeAIAxios/generateCopysAxios";
import ConfirmationDialog from "../payment/ConfirmationDialog";
import gifs from "../../assets/icons/gifs.json";
import Modal from "../payment/modal";
import { duplicateFunnel, getSectionsGenerated, getWebsiteVisitsSummary, recoverFunnelGeneration, retrySection, WebsiteVisitSummary } from "../../services/funnels";
import { FunnelsCard } from "../cards/FunnelsCard";
import FunnelCardSkeleton from "../funnel/FunnelCardSkeleton";
import { dictionary } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";
import { getSubDomain, getWebsitePublicUrl } from "../../services/subDomain/subDomain";
import { useTutorialContext } from "../../context/TutorialContext";
import { Product, ProductType } from "../../interfaces/websites";
import { database } from "../../lib/firebase";
import { ref, onValue } from "firebase/database";
import { useRealtimeEnvironment } from "../../hooks/useRealtimeEnvironment";
momentum.register();

type WebsitesGalleryProps = Record<string, never>;

interface SectionsGenerated {
  done: boolean;
  website_id: string;
  has_errors?: boolean;
  error_sections?: string[];
  generation_status?: string | null;
  failure_stage?: string | null;
  failure_reason?: string | null;
  failure_code?: string | null;
  retry_available?: boolean;
  recovery_action?: string | null;
}

const PAGE_SIZE = 15;

const openFunnelDestination = (url: string) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

const WebsitesGallery: React.FC<WebsitesGalleryProps> = () => {
  const { language } = useAuth();
  const { websites, isLoading, loadError, initialize } = useWebsitesStore();
  const realtimeEnvironment = useRealtimeEnvironment();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const hasMore = visibleCount < websites.length;

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, websites.length));
  }, [websites.length]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, hasMore]);

  useEffect(() => {
    if (!hasMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const checkIfAlreadyVisible = () => {
      const rect = sentinel.getBoundingClientRect();
      if (rect.top <= window.innerHeight + 220) {
        loadMore();
      }
    };

    checkIfAlreadyVisible();
    const timeout = window.setTimeout(checkIfAlreadyVisible, 150);
    return () => window.clearTimeout(timeout);
  }, [hasMore, loadMore, visibleCount, websites.length]);

  const [websiteIdToDelete, setWebsiteIdToDelete] = useState<string>("");
  const [openModalDelete, setOpenModalDelete] = useState<boolean>(false);
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
  const [, setLoadingDuplicate] = useState<string | null>(null);
  const [sectionsGenerated, setSectionsGenerated] = useState<
    SectionsGenerated[]
  >([]);
  const [visitSummariesByWebsiteId, setVisitSummariesByWebsiteId] = useState<Record<string, WebsiteVisitSummary>>({});

  const navigate = useNavigate();

  useEffect(() => {
    initialize();
  }, [initialize]);

  const websitesWithSalesKey = useMemo(
    () => websites
      .filter((website) => (website.salesSummary?.salesCount ?? 0) > 0)
      .map((website) => website.websiteId)
      .sort()
      .join("|"),
    [websites]
  );

  useEffect(() => {
    if (isLoading) return;
    const websiteIds = websitesWithSalesKey.split("|").filter(Boolean);
    if (websiteIds.length === 0) {
      setVisitSummariesByWebsiteId({});
      return;
    }

    let cancelled = false;
    getWebsiteVisitsSummary(websiteIds)
      .then((response) => {
        if (cancelled) return;
        const nextSummaries = response.summaries.reduce<Record<string, WebsiteVisitSummary>>((acc, summary) => {
          acc[summary.website_id] = summary;
          return acc;
        }, {});
        setVisitSummariesByWebsiteId(nextSummaries);
      })
      .catch((error) => {
        if (cancelled) return;
        console.warn("Could not load funnel visit summaries:", error);
        setVisitSummariesByWebsiteId({});
      });

    return () => {
      cancelled = true;
    };
  }, [isLoading, websitesWithSalesKey]);

  const visibleWebsites = useMemo(
    () => websites.slice(0, visibleCount),
    [websites, visibleCount]
  );

  const handleOpenModal = (id: string) => {
    setOpenModalDelete(true);
    setWebsiteIdToDelete(id);
  };

  const handleDelete = (id: string) => {
    setLoadingDelete(true);
    const userId = decodeTokenService.getUserIdFromToken() || "";
    deleteWebsite(userId, id)
      .then(() => {
        invalidateWebsitesCache();
        enqueueSnackbar("Funnel deleted successfully.", {
          variant: "success",
        });
        setTimeout(() => {
          setOpenModalDelete(false);
          window.location.reload();
        }, 1000);
      })
      .catch((error) => {
        console.error("Error deleting funnel:", error);
        enqueueSnackbar("Error deleting funnel.", {
          variant: "error",
        });
      })
      .finally(() => {
        setLoadingDelete(false);
      });
  };

  const handleEdit = async (websiteId: string) => {
    try {
      const url = handleUrlBuilder(websiteId);
      openFunnelDestination(url);
    } catch (error) {
      console.error("Failed to fetch SSO URL:", error);
      toast.error("Error al cargar la página.");
    }
  };

  const handlePreview = async (websiteId: string) => {
    const fallbackUrl = handleUrlPreview(websiteId);

    const previewWindow = window.open("about:blank", "_blank");
    const navigatePreview = (url: string) => {
      if (previewWindow) {
        previewWindow.opener = null;
        previewWindow.location.href = url;
        return;
      }
      openFunnelDestination(url);
    };

    try {
      const publicUrl = await getWebsitePublicUrl(websiteId);
      if (publicUrl?.primary_public_url) {
        navigatePreview(publicUrl.primary_public_url);
        return;
      }
    } catch (error) {
      console.warn("Failed to resolve primary public URL, using legacy subdomain fallback.", error);
    }

    try {
      const subDomain = await getSubDomain(websiteId);
      const funnelsDomain = import.meta.env.VITE_DOMIAN_FUNNELS;

      if (subDomain?.subdomain && funnelsDomain) {
        navigatePreview(
          `https://${subDomain.subdomain}.${funnelsDomain}`
        );
        return;
      }
    } catch (error) {
      console.warn("Failed to resolve funnel subdomain, using preview URL.", error);
    }

    navigatePreview(fallbackUrl);
  };

  const handleUrlBuilder = (websiteId: string) => {
    const userId = decodeTokenService.getUserIdFromToken() || "";
    const token = localStorage.getItem("access_token") || "";
    const url =
      import.meta.env.VITE_REACT_APP_URL_BUILDER +
      `builder/funnel/${userId}/${websiteId}?token=${token}`;
    return url;
  };

  const handleUrlPreview = (websiteId: string) => {
    const userId = decodeTokenService.getUserIdFromToken() || "";
    const url =
      import.meta.env.VITE_REACT_APP_URL_BUILDER +
      `user/${userId}/funnel/${websiteId}`;
    return url;
  };

  const handleDuplicate = (websiteId: string) => {
    setLoadingDuplicate(websiteId);
    duplicateFunnel(websiteId)
      .then(() => {
        invalidateWebsitesCache();
        enqueueSnackbar("Funnel duplicated successfully.", {
          variant: "success",
        });
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      })
      .catch(() => {
        enqueueSnackbar("Error duplicating funnel.", {
          variant: "error",
        });
      });
  };

  const handleAdsClick = (products: Product[]) => {
    const principalProduct = products.find((p) => p.type === ProductType.PRINCIPAL);
    if (!principalProduct) return navigate("/ads");
    navigate(`/ads/product/${principalProduct.product_id}`);
  };

  const mergeSectionsGenerated = useCallback((updates: SectionsGenerated[], options?: { allowDoneRegression?: boolean }) => {
    setSectionsGenerated((current) => {
      const generatedById = new Map(
        current.map((section) => [section.website_id, section])
      );
      updates.forEach((section) => {
        const existing = generatedById.get(section.website_id);
        if (
          existing?.done === true &&
          section.done === false &&
          options?.allowDoneRegression !== true
        ) {
          generatedById.set(section.website_id, {
            ...existing,
            has_errors: section.has_errors ?? existing.has_errors,
            error_sections: section.error_sections ?? existing.error_sections,
            generation_status: section.generation_status ?? existing.generation_status,
            failure_stage: section.failure_stage ?? existing.failure_stage,
            failure_reason: section.failure_reason ?? existing.failure_reason,
            failure_code: section.failure_code ?? existing.failure_code,
            retry_available: section.retry_available ?? existing.retry_available,
            recovery_action: section.recovery_action ?? existing.recovery_action,
          });
          return;
        }
        generatedById.set(section.website_id, { ...existing, ...section });
      });
      return Array.from(generatedById.values());
    });
  }, []);

  const handleRetryFunnel = useCallback(
    async (websiteId: string, errorSections: string[], recoveryAction?: string | null) => {
      mergeSectionsGenerated(
        [
          {
            website_id: websiteId,
            done: false,
            has_errors: false,
            error_sections: [],
            generation_status: "recovering",
            retry_available: false,
            recovery_action: recoveryAction,
          },
        ],
        { allowDoneRegression: true }
      );

      try {
        if (errorSections.length === 0) {
          await recoverFunnelGeneration(websiteId);
          enqueueSnackbar(dictionary["retry-funnel-success"][language], {
            variant: "success",
          });
          return;
        }

        const results = await Promise.allSettled(errorSections.map((name) => retrySection(websiteId, name)));
        const failed = results.filter((r) => r.status === "rejected").length;
        if (failed !== results.length) {
          enqueueSnackbar(dictionary["retry-funnel-success"][language], {
            variant: "success",
          });
          return;
        }
      } catch {
        mergeSectionsGenerated(
          [
            {
              website_id: websiteId,
              done: false,
              has_errors: true,
              error_sections: errorSections,
              generation_status: "failed",
              retry_available: true,
              recovery_action: recoveryAction,
            },
          ],
          { allowDoneRegression: true }
        );
        enqueueSnackbar(dictionary["retry-funnel-error"][language], {
          variant: "error",
        });
        return;
      }

      mergeSectionsGenerated(
        [
          {
            website_id: websiteId,
            done: false,
            has_errors: true,
            error_sections: errorSections,
            generation_status: "failed",
            retry_available: true,
            recovery_action: recoveryAction,
          },
        ],
        { allowDoneRegression: true }
      );
      enqueueSnackbar(dictionary["retry-funnel-error"][language], {
        variant: "error",
      });
    },
    [language, mergeSectionsGenerated]
  );

  const handleSectionsGenerated = useCallback(() => {
    if (visibleWebsites.length === 0) {
      return;
    }

    const data = visibleWebsites.map((website) => website.websiteId);
    getSectionsGenerated(data)
      .then((response) => {
        mergeSectionsGenerated(response);
      })
      .catch((error) => {
        console.error("Error fetching sections generated:", error);
      });
  }, [mergeSectionsGenerated, visibleWebsites]);

  const websiteIdsKey = useMemo(
    () => visibleWebsites.map((website) => website.websiteId).join("|"),
    [visibleWebsites]
  );

  useEffect(() => {
    if (!isLoading) {
      handleSectionsGenerated();
    }
  }, [isLoading, websiteIdsKey, handleSectionsGenerated]);

  useEffect(() => {
    if (isLoading || visibleWebsites.length === 0 || !realtimeEnvironment) return;

    const unsubscribers = visibleWebsites.map((website) => {
      const dataRef = ref(
        database,
        `${realtimeEnvironment}/websites/${website.websiteId}/sections-generated`
      );

      return onValue(dataRef, (snapshot) => {
        const value = snapshot.val();
        if (typeof value?.done !== "boolean") return;
        mergeSectionsGenerated([
          {
            website_id: website.websiteId,
            done: value.done,
            generation_status: value.status,
            failure_code: value.failure_code,
            retry_available: value.retry_available,
          },
        ]);
      });
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [isLoading, mergeSectionsGenerated, realtimeEnvironment, visibleWebsites, websiteIdsKey]);

  useEffect(() => {
    if (isLoading || visibleWebsites.length === 0) return;
    const intervalId = window.setInterval(handleSectionsGenerated, 15000);
    return () => window.clearInterval(intervalId);
  }, [handleSectionsGenerated, isLoading, visibleWebsites.length]);

  const websitesWithGenerated = useMemo(() => websites.map((website) => {
    const match = sectionsGenerated.find(
      (section) => section.website_id === website.websiteId
    );
    const visitSummary = visitSummariesByWebsiteId[website.websiteId];
    const salesCount = website.salesSummary?.salesCount ?? 0;
    const uniqueVisits = visitSummary?.unique_visits ?? 0;
    const conversionRate = salesCount > 0 && uniqueVisits > 0
      ? Number(((salesCount / uniqueVisits) * 100).toFixed(2))
      : null;

    return {
      ...website,
      salesSummary: website.salesSummary
        ? {
          ...website.salesSummary,
          conversionRate,
        }
        : website.salesSummary,
      generated: match ? match.done : website.generated ?? false,
      hasErrors: match?.has_errors ?? false,
      errorSections: match?.error_sections ?? [],
      generationStatus: match?.generation_status ?? website.generationStatus,
      failureStage: match?.failure_stage ?? website.failureStage,
      failureReason: match?.failure_reason ?? website.failureReason,
      failureCode: match?.failure_code ?? website.failureCode,
      retryAvailable: match?.retry_available ?? website.retryAvailable,
      recoveryAction: match?.recovery_action ?? website.recoveryAction,
    };
  }), [websites, sectionsGenerated, visitSummariesByWebsiteId]);

  const { isPaused, setCurrentStepIndex, resumeTutorial } = useTutorialContext();
  const isReady = !isLoading;

  useEffect(() => {
    const checkIfPaused = async () => {
      if (websitesWithGenerated.length > 0 && isReady && isPaused) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        setCurrentStepIndex(5);
        resumeTutorial();
      }
    };
    checkIfPaused();
  }, [websitesWithGenerated, isReady, isPaused, setCurrentStepIndex, resumeTutorial]);

  return (
    <div className="w-full">
      {openModalDelete && (
        <Modal
          onClose={() => {
            setOpenModalDelete(false);
          }}
        >
          <ConfirmationDialog
            loading={!loadingDelete}
            title={
              dictionary["are-you-sure-you-want-to-delete-this-funnel"][
              language
              ]
            }
            description={dictionary["this-action-cannot-be-undone"][language]}
            actionText={dictionary["delete"][language]}
            handleAction={() => {
              handleDelete(websiteIdToDelete);
            }}
            handleCancel={() => {
              setOpenModalDelete(false);
            }}
            cancelText="Cancel"
            imageSrc={gifs.delete}
          />
        </Modal>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading
          ? Array.from({ length: 9 }).map((_, index) => (
            <FunnelCardSkeleton key={index} />
          ))
          : loadError && websites.length === 0 ? (
            <div className="col-span-full rounded-2xl border border-red-100 bg-white px-6 py-8 text-center shadow-sm">
              <p className="text-sm font-semibold text-gray-900">
                No pudimos cargar tus embudos
              </p>
              <p className="mx-auto mt-2 max-w-md text-sm text-gray-500">
                Hubo un error temporal consultando el listado. Intenta de nuevo en unos segundos.
              </p>
              <button
                type="button"
                onClick={() => initialize()}
                className="mx-auto mt-5 inline-flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-gray-800"
              >
                <RefreshCw className="h-4 w-4" />
                Reintentar
              </button>
            </div>
          )
          : websitesWithGenerated.slice(0, visibleCount).map((website, index) => (
            <FunnelsCard
              key={website.websiteId}
              id={website.websiteId}
              idHtml={index === 0 ? "summaryTutorialCreateSalesPage-target" : ""}
              idSequence={website.idSequence}
              product={website.products}
              description={website.sales_angle_description}
              isActive={website.configurations.enabled}
              createdAt={website.createdAt}
              handleDuplicate={() => handleDuplicate(website.websiteId)}
              handleEdit={() => handleEdit(website.websiteId)}
              handleOpenModal={() => handleOpenModal(website.websiteId)}
              handlePreview={() => handlePreview(website.websiteId)}
              handleAdsClick={() => handleAdsClick(website.products)}
              sectionsGenerated={website.generated}
              hasErrors={website.hasErrors}
              errorSections={website.errorSections}
              generationStatus={website.generationStatus}
              failureReason={website.failureReason}
              retryAvailable={website.retryAvailable}
              onRetry={() => handleRetryFunnel(website.websiteId, website.errorSections, website.recoveryAction)}
              configurations={website.configurations}
              nameFunnel={website.name}
              productName={website.productName}
              productImage={website.productImage}
              salesSummary={website.salesSummary}
            />
          ))}
      </div>

      {/* Infinite scroll sentinel */}
      {!isLoading && websites.length > 0 && (
        <div ref={sentinelRef} className="flex justify-center py-8">
          {hasMore ? (
            <button
              type="button"
              onClick={loadMore}
              className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-500 shadow-sm transition hover:border-gray-300 hover:text-gray-700"
            >
              <span>
                {dictionary["load-more"]?.[language] ?? "Cargar más"}
              </span>
            </button>
          ) : (
            <p className="text-xs text-gray-300">
              {websites.length} {dictionary["funnels"][language]}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default WebsitesGallery;
